import { useTranslation } from "react-i18next";
import { axiosInstance } from "api/axios";
import CaseCard from "components/CaseCard";
import SortOrder from "components/SortOrder";
import {
  EOrder,
  ETypeOfOrder,
  IOrderObject,
} from "components/SortOrder/typings";
import CasesContext from "contexts/CasesContext";
import WorkspaceContext from "contexts/CurrentWorkspace";
import { ICaseSimple } from "dto/Cases/ICaseSimple";
import { useContext, useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useDebounce } from "use-debounce";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import { useParams } from "react-router-dom";
import { List } from "@mui/material";
import NotFoundScreen from "components/NotFoundScreen";
import CasesSkeleton from "components/CasesSkeleton";
import { logger } from "utils/logger";
import ModalsManagerContext from "contexts/ModalsManager";
import { EStatusGlobalType } from "dto/IStatus";
import useCurrentCompanyId from "hooks/useCurrentCompanyId";

const CaseList: FC<Props> = ({
  tags,
  onlyDesk = false,
  onClick,
  statuses,
  setSelectedEdit,
  searchText,
  selectedThread,
  setSelectedThread,
  createNewThread,
  setCreateNewThread,
  entityId = null,
  statusId = null,
  tagId = null,
}) => {
  const { t } = useTranslation();
  const currentCompanyId = useCurrentCompanyId();
  const { caseId } = useParams();
  const { cases: casesContext, setCases: setCasesContext } =
    useContext(CasesContext);
  const { modals, setModals } = useContext(ModalsManagerContext);

  const [isInitialized, setIsInitialized] = useState(false);

  const [prevPage, setPrevPage] = useState<number | null>(null);
  const [, setNextPage] = useState(0);

  const [debouncedText] = useDebounce(searchText, 500);

  const { filterId } = useContext(WorkspaceContext);

  const [listFilter, setListFilter] = useState<IOrderObject>({
    type: ETypeOfOrder.CREATION_DATE,
    order: EOrder.DESCENDING,
    initalized: false,
  });

  const [forId, setForId] = useState<string | null | undefined>(caseId);
  const [lastSeen, setLastSeen] = useState<string | null>(null);

  const [topItemId, setTopItemId] = useState<string | null>(null);
  const [keepItemId, setKeepItemId] = useState<string | null>(null);
  const [previousScrollPosition, setPreviousScrollPosition] =
    useState<number>(null);

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    isLoading,
    isFetchingNextPage,
    hasPreviousPage,
    refetch,
  } = useInfiniteQuery(
    ["cases", listFilter, filterId, debouncedText, entityId, statusId, tagId],
    async ({ pageParam = 0 }) => {
      logger.log(`Loading cases page: ${pageParam}, forId: ${forId}`);
      const query = new URLSearchParams(window.location.search);
      if (debouncedText.length) {
        query.append("Text", debouncedText);
      }

      if (filterId) {
        query.append("FilterId", filterId);
      }
      query.append("Page", pageParam);

      query.append("SortProp", listFilter.type);
      query.append("SortOrder", listFilter.order);

      if (statusId) {
        query.append("StatusId", statusId);
      }

      if (tagId) {
        query.append("TagId", tagId);
      }

      if (entityId) {
        query.append("ParticipantId", entityId);
      }

      if (!filterId && !entityId && !statusId) {
        query.append(
          "WithoutGlobalStatus",
          EStatusGlobalType.Case_Archival.toString(),
        );
      }

      if (forId) {
        query.append("PageForId", forId);
      }

      if (onlyDesk) {
        query.append("Desk", "true");
      }

      const res = await axiosInstance.get(`/cases?${query}`);

      return {
        items: res.data,
        page: res.data.Page,
        LastPage: res.data.LastPage,
        nextPage: res.data.Page + 1,
      };
    },

    {
      staleTime: 0,
      cacheTime: 0,
      enabled: false,

      getPreviousPageParam: (firstPage) => {
        if (firstPage?.page > 0) {
          return firstPage.page - 1;
        }
        return undefined;
      },
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.page + 1;
        }
        return undefined;
      },
    },
  );

  useEffect(() => {
    if (data?.pages?.[0] && forId) {
      setForId(null);
    }
  }, [data]);

  useEffect(() => {
    if (!listFilter.initalized) return;

    setNextPage(0);
    setCasesContext([]);
    setTimeout(() => {
      refetch();
    }, 100);
  }, [listFilter, currentCompanyId]);

  useEffect(() => {
    refetch();
  }, [filterId]);

  useEffect(() => {
    setNextPage(0);

    setTimeout(() => {
      refetch();
    }, 100);
  }, [debouncedText, tagId, statusId, entityId]);

  const compileData = async () => {
    if (!data) return;

    setNextPage(data.pages[data.pages.length - 1].nextPage);
    if (!prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }

    if (prevPage && data.pages[data.pages.length - 1].page <= prevPage) {
      setPrevPage(data.pages[data.pages.length - 1].page - 1);
    }
    const packs = await Promise.all(
      data?.pages
        .sort((a, b) => {
          return a.page - b.page;
        })
        .map((page) => {
          return page.items.Items;
        })
        .flat(),
    );

    setCasesContext(packs);
    setIsInitialized(true);
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    setCasesContext([]);
  }, [searchText]);

  const separatorRef = useRef<HTMLDivElement>(null);
  const [selectedElements, setSelectedElements] = useState<ICaseSimple[]>([]);
  const [canSelect, setCanSelect] = useState(false);

  const changeToSelectable = (e: any) => {
    if (e.code === "ControlLeft" || e.code === "ShiftLeft") {
      setCanSelect(true);
    }
  };

  const changeToNotSelectable = (e: any) => {
    setCanSelect(false);
  };
  useEffect(() => {
    window.addEventListener("keydown", changeToSelectable);
    window.addEventListener("keyup", changeToNotSelectable);

    return () => {
      window.removeEventListener("keydown", changeToSelectable);
      window.removeEventListener("keyup", changeToNotSelectable);
    };
  }, []);

  useEffect(() => {
    const lastTopItem = topItemId;

    if (casesContext.length > 0) {
      const newId = casesContext[0].Id;
      setTopItemId(newId);

      if (newId !== lastTopItem) {
        const c = scrollWrapper.current;
        const divWithIdOne = c.querySelector(`div[data-id="${newId}"]`);

        if (divWithIdOne) {
          //@ts-ignore
          const position = divWithIdOne.offsetTop;
          c.scrollTop = position;
        }
      }
    } else {
      setTopItemId(null);
    }
  }, [casesContext]);

  useEffect(() => {
    if (keepItemId && previousScrollPosition) {
      const c = scrollWrapper.current;
      const divWithIdOne = c.querySelector(`div[data-id="${keepItemId}"]`);

      if (divWithIdOne) {
        c.scrollTop = c.scrollHeight - previousScrollPosition;
      }

      setKeepItemId(null);
    }
  }, [casesContext]);

  useEffect(() => {
    const c = scrollWrapper.current;

    if (!c || !lastSeen) return;

    const divWithIdOne = c.querySelector(`div[data-id="${lastSeen}"]`);

    if (divWithIdOne) {
      //@ts-ignore
      const position = divWithIdOne.offsetTop;
      c.scrollTop = position;
      setLastSeen(null);
    }
  }, [lastSeen, casesContext]);

  const scrollWrapper = useRef(null);

  const gridRef = useRef(null);

  useEffect(() => {
    if (!gridRef?.current) return;
    //@ts-ignore
    gridRef.current.selectRow(caseId, true);

    const index = casesContext.findIndex((c) => c.Id === caseId);
    //@ts-ignore
    gridRef?.current?.scrollToIndexes(index);
  }, [gridRef, casesContext]);

  const [lastFetchTime, setLastFetchTime] = useState(0);
  const fetchThreshold = 1000; // 1 second

  const handleLoadNextPage = () => {
    if (!separatorRef.current || isLoading) return;

    const rect = separatorRef.current.getBoundingClientRect();

    const isSeparatorVisible =
      rect.top * 0.5 < window.innerHeight && rect.bottom >= 0;

    const currentTime = Date.now();
    if (currentTime - lastFetchTime < fetchThreshold) return;

    if (hasPreviousPage && scrollWrapper.current.scrollTop < 150) {
      setLastFetchTime(currentTime);
      if (casesContext.length) {
        setKeepItemId(casesContext[0].Id);
        setPreviousScrollPosition(
          scrollWrapper.current.scrollHeight - scrollWrapper.current.scrollTop,
        );
      }
      fetchPreviousPage();
      return;
    }

    if (isSeparatorVisible) {
      setLastFetchTime(currentTime);
      fetchNextPage();
    }
  };

  const isFirstLoading = isLoading && !isFetchingNextPage;

  const uniquePackages: ICaseSimple[] =
    Array.from(new Set(casesContext.map((pkg) => pkg.Id))).map((id) => {
      return casesContext.find((pkg) => pkg.Id === id);
    }) ?? [];

  const [, setIsOverflowed] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    const el = scrollWrapper.current;
    if (el) {
      const hasOverflow =
        el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
      setIsOverflowed(hasOverflow && uniquePackages.length > 0);

      if (!hasOverflow && hasPreviousPage) {
        handleLoadNextPage();
      }
    }
  }, [scrollWrapper, uniquePackages, isLoading, hasPreviousPage]);

  return (
    <div>
      <div className={styles.order}>
        <SortOrder
          isLoading={false}
          refetch={() => {}}
          setListFilter={setListFilter}
          type={listFilter.type}
          order={listFilter.order}
        />
      </div>
      <div className={styles.packageList}>
        <List
          ref={scrollWrapper}
          onScroll={() => {
            handleLoadNextPage();
          }}
          sx={{
            width: "100%",
            position: "relative",
            overflow: "auto",
            maxHeight: "calc(100% )",
            "& ul": { padding: 0 },
          }}
        >
          {isLoading &&
            Array.from({ length: 100 }, (_, index) => (
              <CasesSkeleton key={index} />
            ))}

          {!casesContext.length &&
            !isFirstLoading &&
            !isLoading &&
            !isFetchingNextPage &&
            isInitialized && (
              <div style={{ height: "calc(100vh - 180px)" }}>
                <NotFoundScreen
                  title={
                    onlyDesk
                      ? t("emptyStates.deskEmpty")
                      : t("emptyStates.noResults")
                  }
                  text={
                    onlyDesk
                      ? t("emptyStates.deskEmptyText")
                      : t("emptyStates.noResultsText")
                  }
                  buttonName={onlyDesk ? t("cases.createNew") : null}
                  onClick={() => setModals({ ...modals, createCase: true })}
                />
              </div>
            )}
          {uniquePackages.map((caseItem) => {
            return (
              <CaseCard
                selectedThread={selectedThread}
                setSelectedThread={setSelectedThread}
                key={caseItem.Id}
                className={styles.card}
                c={caseItem}
                tags={tags}
                onClick={onClick ? () => onClick(caseItem) : undefined}
                statuses={statuses}
                setSelectedEdit={setSelectedEdit}
                isSelectable={canSelect}
                selectedElements={selectedElements}
                setSelectedElements={setSelectedElements}
                createNewThread={createNewThread}
                setCreateNewThread={setCreateNewThread}
                setForId={setForId}
                searchText={searchText}
                inDeskView={onlyDesk}
              />
            );
          })}
          <div ref={separatorRef} style={{ height: "5px" }}></div>
        </List>
      </div>
    </div>
  );
};

export default CaseList;
